<template>
  <o-modal :active="isActive" :canCancel="false" class="modal-sm">
    <div class="modal-card">
      <header class="modal-card-head">
        <h4 class="modal-card-title line-height">
          {{editMode ? 'Update Location' : 'Add New Location' }}
          <span
            class="line-height required-label is-size-5 is-italic is-pulled-right mr-0 has-text-weight-normal"
          >
            * required
          </span>
        </h4>
      </header>
      <div class="modal-card-body is-visible p-0">
        <div class="column">
          <div class="columns">
            <div class="column pt-0">
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3"> Location Name *</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      v-model.trim="location.name"
                      placeholder="Enter name (3 or more characters)"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label">Owner *</label>
                </div>
                <div class="field-body">
                  <div class="field is-expanded">
                    <div class="control">
                      <input
                      class="input"
                      v-model.trim="location.owner"
                      placeholder="Enter owner name (3 or more characters)"
                    />
                    </div>
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label mb-1">
                  <label class="label">Project Inventory Default</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <o-checkbox v-model="location.defaultInventoryLocation" class="mx-0 p-0">
                    </o-checkbox>
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label">Type</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <mf-multi-select
                      v-model="location.location_type"
                      placeholder="Add location types"
                      :options="locationType"
                      :multiple="true"
                      :tags="true"
                      :close-on-select="false"
                      label="label"
                      track-by="value"
                      @update:modelValue="onLocationTypeChange"
                      class="pendo-assignee-select"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="column pt-0">
              <div class="field">
                <div class="field-label">
                  <label class="label">Street *</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      v-model.trim="location.street"
                      placeholder="Enter street address"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label">City *</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      v-model.trim="location.city"
                      placeholder="Enter city"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label">State *</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      v-model.trim="location.state"
                      placeholder="Enter state"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label">Zip *</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      v-model.trim="location.zip"
                      placeholder="Enter zip code"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="modal-card-foot is-justify-content-flex-end is-align-items-center">
        <div class="column is-narrow">
          <div>
            <button class="button is-outlined" @click="$emit('close')">
              Cancel
            </button>
            <button
              class="button has-background-black-bis"
              @click="AddLocation"
              :disabled="disableSave()"
            >
              {{editMode ? 'Update' : 'Add Location' }}
            </button>
          </div>
        </div>
      </footer>
      <o-loading
        :full-page="true"
        :active="isLoading"
        :can-cancel="true"
      ></o-loading>
    </div>
  </o-modal>
</template>

<script>

import { useStore } from "vuex";
import { reactive, onBeforeMount, toRefs } from "vue";
import { useRouter, useRoute } from 'vue-router';
import { useToast } from "vue-toastification";
import _ from "lodash";

import Locations from '@/models/Locations';

export default {
  name: "CreateLocation",
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    project: {
      type: Object,
      default: () => {},
    },
    editMode: {
      type: Boolean,
      default: false
    },
    rowToEdit: {
      type: Object,
      default: () => {}
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const toast = useToast();
    const locationType = [ 
      {
        label: 'Rental',
        value: 'Rental',
      },
      {
        label: 'Fabrication',
        value: 'Fabrication',
      },
      {
        label: 'Storage',
        value: 'Storage',
      },
      {
        label: 'JobSite',
        value: 'JobSite',
      },
    ];
    const data = reactive({
      location: {
        name: '',
        owner: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        defaultInventoryLocation: false,
        location_type: [],
      },
      isLoading: false,
      usersEmail: [],
      user: store.state.userData,
      projectMember: false,
    });

    const AddLocation = async () => {
      try {
        if (validations(data.location)) {
          data.isLoading = true;
        } else {
          return;
        }
        data.location.company = data.user.company._id || data.user.company;

        if (!_.isEmpty(props.project) && props.project._id) {
          const member = _.find(
            props.project.projectUsers,
            (user) => user.user.email === data.user.email,
          );
          if (member) data.projectMember = true;
          data.location.type = 'project_location';
          data.location.project = [props.project._id]; 
        } else {
          data.location.type = 'company_location';
        }
        if (!_.isEmpty(data.location.location_type)) {
            data.location.location_type = data.location.location_type.map((t) => t.value)
        }
        if (props.editMode) {
          const row = _.cloneDeep(props.rowToEdit)
          Object.assign(row, data.location)
          await Locations.update(row, data.projectMember);
          toast.success('Location Updated')
        } else {
          await Locations.update(data.location, data.projectMember);
          toast.success('Location Added')
        }
        emit('close', {added: true});
      } catch(e) {
        console.error('Error creating/editing location: ', e);
        toast.error('Error: ', e._message || e);
      } finally {
        data.isLoading = false;
      }
    }

    const validations = (location) => {
      if (_.isEmpty(location.name) || hasValidLength(location.name, 3)) {
        toast.error('Location name should be atleast 3 characters long!');
        return false;
      } if (_.isEmpty(location.owner) || hasValidLength(location.owner, 3)) {
        toast.error('Owner name should be atleast 3 characters long!');
        return false;
      } if (_.isEmpty(location.street) || hasValidLength(location.street, 3)) {
        toast.error('Street name should be atleast 3 characters long!');
        return false;
      } if (_.isEmpty(location.city) || hasValidLength(location.city, 3)) {
        toast.error('City name should be atleast 3 characters long!');
        return false;
      } if (_.isEmpty(location.state) || hasValidLength(location.state, 2)) {
        toast.error('State name should be atleast 2 characters long!');
        return false;
      } if (!validateZip(location.zip, true)) {
        return false;
      }
      return true;
    }

    const hasValidLength = (field, min) => {
      return field.length < min;
    };

    const validateZip = (zipCode, showError) => {
			zipCode = !_.isEmpty(zipCode) ? zipCode : '';
			if (!_.inRange(zipCode.length, 1, 25)) {
				if (showError) {
					toast.error('Valid range for Zip length is 1 - 24!')
				}
				return false;
			}
			return true;
		}

    const disableSave = () => {
      if (_.isEmpty(data.location.name) || _.isEmpty(data.location.owner) || _.isEmpty(data.location.street) || _.isEmpty(data.location.city) || _.isEmpty(data.location.state) || _.isEmpty(data.location.zip)) {
        return true;
      }
      return false;
    }

    const onLocationTypeChange = (selectedValues) => {
      const normalized = _.chain(selectedValues || [])
        .map((item) => {
          if (_.isString(item)) {
            return _.find(locationType, (lt) => lt.value === item || lt.label === item);
          }
          if (_.isObject(item) && !_.isEmpty(item.value)) {
            return _.find(locationType, (lt) => lt.value === item.value) || item;
          }
          return null;
        })
        .compact()
        .uniqBy('value')
        .value();

      data.location.location_type = normalized;
    }

    onBeforeMount(() => {
      if (props.editMode && !_.isEmpty(props.rowToEdit)) {
        const objToAssign = _.pick(props.rowToEdit, ['name', 'owner', 'street', 'city', 'state', 'zip', 'defaultInventoryLocation'])
        Object.assign(data.location, objToAssign)
        if (!_.isEmpty(props.rowToEdit?.location_type)) {
          const mappedTypes = []
          props.rowToEdit?.location_type.forEach(existingLocType => {
            const result = _.find(locationType, (lt) => lt.value === existingLocType)
            if (!_.isEmpty(result)) mappedTypes.push(result)
          });
          if (!_.isEmpty(mappedTypes)) {
            data.location.location_type = mappedTypes
          }
        }
      }
    })

    return {
      ...toRefs(data),
      AddLocation,
      hasValidLength,
      locationType,
      validateZip,
      disableSave,
      onLocationTypeChange,
    }
  }
}
</script>
<style>
.modal-card-head {
  background-color: #fff !important;
  border-bottom: none !important;
}
.modal-card-foot {
  border-top: 1px solid #84888c !important;
  background-color: #fff !important;
}
</style>